<template>
  <div>
    <div class="d-flex align-center" style="min-height: 40px">
      <b>{{ $t('PROJECT_SHEET.TARGETS') }}</b>
      <v-btn-icon v-if="userIsProjectAdmin" size="small" class="ml-auto export-hidden" @click="isEditing = ! isEditing">
        <v-icon>{{ isEditing ? 'far fa-close' : 'far fa-pencil-alt' }}</v-icon>
      </v-btn-icon>
    </div>

    <v-card class="pa-6 flex-grow-1">
      <template v-if="isEditing">
        <div v-for="(target, index) in targetsList" :key="target.id" :class="{ 'mt-4': index }">
          <date-field v-if="target.type == 'date'" v-model="targetsEditValues[target.id]" :label="target.label" :prepend-inner-icon="target.icon"
                      variant="outlined" density="comfortable" hide-details clearable
                      @update:model-value="saveTargets()">
          </date-field>
          <template v-if="target.type == 'budget'">
            <div class="mb-1"><label :for="`targetBudget-${target.id}`">{{ target.label }}</label></div>
            <v-text-field v-model.number="targetsEditValues[target.id]" :id="`targetBudget-${target.id}`" :prepend-inner-icon="target.icon"
                          type="number" :step="100" variant="outlined" density="comfortable" hide-details
                          @change="saveTargets()">
            </v-text-field>
          </template>
        </div>
      </template>
      <div v-else class="d-flex flex-column gap-4">
        <template v-for="(target, index) in targetsList" :key="target.id">
          <v-divider v-if="index"></v-divider>
          <div class="d-flex align-center gap-4">
            <div :class="targetsStatus[target.id]?.class || 'bg-lightgrey'" class="rounded-circle flex-center-center" style="width: 40px; height: 40px; flex: 0 0 40px">
              <v-icon>{{ target.icon }}</v-icon>
              <v-tooltip v-if="targetsStatus[target.id].formatedRemaining || targetsStatus[target.id].formatedCompareValue" location="left" activator="parent">
                <template v-if="target.type == 'date'">
                  <div v-if="targetsStatus[target.id].formatedRemaining">
                    {{ $t('GLOBAL.DAYS_COUNT', targetsStatus[target.id].formatedRemaining) }} {{ $t(targetsStatus[target.id].remainingLabel) }}
                  </div>
                  <div v-if="targetsStatus[target.id].formatedCompareValue">
                    {{ target.id == 'endtime' ? $t('PROJECT_SHEET.TARGET_LAST_ELEMENT') : $t('ELEMENT-DETAIL.DATE') }} : <span class="nobr">{{ targetsStatus[target.id].formatedCompareValue }}</span>
                  </div>
                </template>
                <template v-else-if="target.type == 'budget'">
                  <div v-if="targetsStatus[target.id].formatedRemaining">
                    {{ $t(targetsStatus[target.id].remainingLabel, { amount: targetsStatus[target.id].formatedRemaining }) }}
                  </div>
                  <div v-if="targetsStatus[target.id].formatedCompareValue">
                    {{ $t('PROJECT_SHEET.TOTAL_BUDGET') }} : <span class="nobr">{{ targetsStatus[target.id].formatedCompareValue }}</span>
                  </div>
                </template>
              </v-tooltip>
            </div>
            <div class="line-height-comfortable">
              {{ target.label }} : <div>{{ targetsStatus[target.id]?.formatedTargetValue || $t('PROJECT_SHEET.NONE') }}</div>
            </div>
          </div>
        </template>
      </div>
    </v-card>
  </div>
</template>

<script>
  import clientConfig from '@/client_customs/config';

  export default {
    props: {
      planning: { type: Object, required: true },
      closeEditing: { type: Boolean, default: false },
    },
    emits: ['update'],
    data() {
      const customTargetsList = clientConfig.projectsheet?.customTargetsList || [];
      return {
        targetsList: [
          ...customTargetsList,
          { id: 'endtime', type: 'date', label: this.$t('PROJECT_SHEET.TARGET_ENDTIME'), icon: 'far fa-hourglass-end', getCompareValue() { return this.projectEndtime; } },
          { id: 'budget', type: 'budget', label: this.$t('PROJECT_SHEET.TARGET_BUDGET'), icon: 'far fa-euro-sign', getCompareValue() { return this.budget; } },
        ],
        isEditing: false,
        targetsEditValues: {},
        userIsProjectAdmin: this.planning.meta.access_right == 'admin',
      };
    },
    computed: {
      targets() {
        const projectsheet = this.planning.projectsheet || {};
        return this.targetsList.reduce((acc, target) => {
          const projectsheetPath = target.path ? projectsheet[target.path] : projectsheet;
          if (target.type == 'date') acc[target.id] = projectsheetPath?.[`target_${target.id}`] || null;
          else if (target.type == 'budget') acc[target.id] = projectsheetPath?.[`target_${target.id}`] || '';
          return acc;
        }, {});
      },
      projectEndtime() {
        let projectEndtime = null;
        this.planning.elements.forEach((element) => {
          const endtime = element.getEndTime?.();
          if (endtime) projectEndtime = projectEndtime ? moment.max(projectEndtime, endtime) : endtime;
        });
        return projectEndtime;
      },
      budget() {
        const budgetByIcons = {};
        this.planning.elements.forEach((element) => {
          (element.getBudgets?.() || []).forEach((budget) => {
            const icon = budget.icon || '';
            if (! budgetByIcons[icon]) budgetByIcons[icon] = { amount: 0, amount_inprogress: 0 };
            budgetByIcons[icon].amount += +budget.amount || 0;
            budgetByIcons[icon].amount_inprogress += +budget.amount_inprogress || 0;
          });
        });
        const maxBudget = Object.keys(budgetByIcons).reduce((acc, icon) => Math.max(acc, budgetByIcons[icon].amount_inprogress, budgetByIcons[icon].amount), 0);
        return maxBudget;
      },
      targetsStatus() {
        return this.targetsList.reduce((acc, target) => {
          const targetValue = this.targets[target.id];
          const compareValue = target.getCompareValue.call(this);
          let formatedTargetValue;
          let formatedCompareValue;
          if (target.type == 'date') {
            if (targetValue) formatedTargetValue = this.$filters.moment(targetValue, 'date');
            if (compareValue) formatedCompareValue = this.$filters.moment(compareValue, 'date');
          } else if (target.type == 'budget') {
            if (targetValue) formatedTargetValue = this.$filters.number(targetValue);
            if (compareValue) formatedCompareValue = this.$filters.number(Math.abs(compareValue));
          }
          if (! compareValue || ! targetValue) {
            acc[target.id] = {
              formatedTargetValue,
              formatedCompareValue,
            };
            return acc;
          }

          let remaining;
          let formatedRemaining;
          let remainingLabel;
          if (target.type == 'date') {
            remaining = moment(targetValue).endOf('day').diff(compareValue, 'days');
            if (remaining) formatedRemaining = Math.abs(remaining);
            remainingLabel = remaining >= 0 ? 'MONITORING_PLAN.AHEAD' : 'MONITORING_PLAN.BEHIND';
          } else if (target.type == 'budget') {
            remaining = targetValue - compareValue;
            if (remaining) formatedRemaining = this.$filters.number(Math.abs(remaining));
            remainingLabel = remaining >= 0 ? 'PROJECT_SHEET.BUDGET_REMAINING_OF' : 'PROJECT_SHEET.BUDGET_EXCEEDED_BY';
          }
          acc[target.id] = {
            formatedTargetValue,
            formatedCompareValue,
            formatedRemaining,
            remainingLabel,
            class: remaining >= 0 ? 'bg-successgreen' : 'bg-errorred',
          };
          return acc;
        }, {});
      },
    },
    watch: {
      isEditing: {
        handler(newVal) {
          if (newVal) this.targetsEditValues = angular.copy(this.targets);
        },
        immediate: true,
      },
      closeEditing(newVal) {
        if (newVal) this.isEditing = false;
      },
    },
    methods: {
      saveTargets() {
        const payload = {};
        this.targetsList.forEach((target) => {
          let newVal = this.targetsEditValues[target.id];
          if (target.type == 'date') newVal = newVal?.endOf?.('day').format() || newVal;

          if (target.path) {
            if (! payload[target.path]) payload[target.path] = { ...this.planning.projectsheet[target.path] };
            payload[target.path][`target_${target.id}`] = newVal; // projectsheet.customFields.target_xxx
          } else {
            payload[`target_${target.id}`] = newVal;
          }
        });

        this.$emit('update', payload);
      },
    },
  };
</script>
